import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Eye,
  X,
  Clock,
  Tag,
  User,
  BookOpen,
  Power,
  PowerOff,
} from "lucide-react";
import { showError, showSuccess } from "../lib/toast";
import {
  server_post_data,
  get_blog_data,
  delete_blog,
  update_blog_status,
} from "../serviceconnection/ServiceConnection";
import BlogForm from "./BlogForm";

export default function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("All");
  const [listLoading, setListLoading] = useState(true);

  const [viewBlog, setViewBlog] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // { open: false } = list | { open: true, editId: null } = add | { open: true, editId: 5 } = edit
  const [formPage, setFormPage] = useState({ open: false, editId: null });
  // ── Toggle Status ────────────────────────────────────────────────────────
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  // ── Derived ───────────────────────────────────────────────────────────────
  const categories = [
    "All",
    ...Array.from(new Set(blogs.map((b) => b.category).filter(Boolean))),
  ];

  const filtered = blogs.filter((b) => {
    const matchSearch =
      b.title_name?.toLowerCase().includes(search.toLowerCase()) ||
      b.author?.toLowerCase().includes(search.toLowerCase()) ||
      b.category?.toLowerCase().includes(search.toLowerCase());
    const matchCat = catFilter === "All" || b.category === catFilter;
    return matchSearch && matchCat;
  });

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchBlogs = async () => {
    try {
      setListLoading(true);
      const fd = new FormData();
      fd.append("main_id", 0);
      const res = await server_post_data(get_blog_data, fd);
      setBlogs(res.data?.data ?? []);
    } catch {
      showError("Failed to load blogs");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    try {
      const fd = new FormData();
      fd.append("p_id", deleteId);
      const res = await server_post_data(delete_blog, fd);
      if (res.data?.error) {
        showError(res.data?.message || "Delete failed");
        return;
      }
      showSuccess("Blog deleted");
      setDeleteId(null);
      setBlogs((prev) => prev.filter((b) => b.primary_id !== deleteId));
    } catch {
      showError("Something went wrong");
    }
  };

  const handleToggleStatus = async (blog) => {
    const newStatus = blog.status === 1 ? 0 : 1;
    try {
      setStatusLoadingId(blog.primary_id);
      const fd = new FormData();
      fd.append("p_id", blog.primary_id);
      fd.append("status", newStatus);
      const res = await server_post_data(update_blog_status, fd);
      if (res.data?.error) {
        showError(res.data?.message || "Status update failed");
        return;
      }
      setBlogs((prev) =>
        prev.map((b) =>
          b.primary_id === blog.primary_id ? { ...b, status: newStatus } : b,
        ),
      );
      showSuccess(newStatus === 1 ? "Blog activated" : "Blog deactivated");
    } catch {
      showError("Something went wrong");
    } finally {
      setStatusLoadingId(null);
    }
  };
  // ── Form page open hone pe BlogForm render karo ───────────────────────────
  if (formPage.open) {
    return (
      <BlogForm
        editId={formPage.editId}
        onBack={() => setFormPage({ open: false, editId: null })}
        onSaved={() => {
          setFormPage({ open: false, editId: null });
          fetchBlogs();
        }}
      />
    );
  }

  // ── List Page ─────────────────────────────────────────────────────────────
  return (
    <div className="max-w-[1400px] mx-auto space-y-5">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Blogs</h1>
          <p className="page-subtitle">
            Manage blog posts and insights published on the website
          </p>
        </div>
        <button
          onClick={() => setFormPage({ open: true, editId: null })}
          className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white"
          style={{ background: "hsl(var(--accent))" }}
        >
          <Plus className="w-4 h-4" /> Write Blog
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          {
            label: "Total Blogs",
            value: blogs.length,
            color: "hsl(var(--accent))",
          },
          {
            label: "Categories",
            value: categories.length - 1,
            color: "hsl(var(--info))",
          },
          {
            label: "This Month",
            value: blogs.filter((b) => {
              const d = new Date(b.entry_date),
                n = new Date();
              return (
                d.getMonth() === n.getMonth() &&
                d.getFullYear() === n.getFullYear()
              );
            }).length,
            color: "hsl(var(--success))",
          },
        ].map((s) => (
          <div key={s.label} className="cms-card p-4">
            <p className="text-2xl font-bold" style={{ color: s.color }}>
              {s.value}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="cms-card p-4 flex flex-wrap items-center gap-3">
        <div className="search-input flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title, author or category..."
            className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCatFilter(c)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                catFilter === c
                  ? "text-white border-transparent"
                  : "border-border text-muted-foreground hover:border-accent/30"
              }`}
              style={
                catFilter === c ? { background: "hsl(var(--accent))" } : {}
              }
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Blog Grid */}
      {listLoading ? (
        <div className="text-center py-16 text-muted-foreground text-sm">
          Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-sm">
          No blogs found
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((b) => (
            <div
              key={b.primary_id}
              className="cms-card p-0 overflow-hidden flex flex-col group"
            >
              {/* Cover */}
              <div className="relative w-full h-44 bg-muted overflow-hidden flex-shrink-0">
                {b.image_url ? (
                  <img
                    src={b.image_url}
                    alt={b.title_name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <BookOpen className="w-10 h-10 text-muted-foreground/30" />
                  </div>
                )}
                {b.category && (
                  <span
                    className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                    style={{ background: "hsl(var(--accent))" }}
                  >
                    {b.category}
                  </span>
                )}
                {/* 🆕 Status badge */}
                <span
                  className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-semibold ${
                    b.status === 1
                      ? "bg-green-500/90 text-white"
                      : "bg-gray-400/90 text-white"
                  }`}
                >
                  {b.status === 1 ? "Active" : "Inactive"}
                </span>
              </div>

              {/* Content */}
              <div className="p-4 flex flex-col flex-1">
                <h3 className="font-semibold text-foreground text-sm leading-snug line-clamp-2 mb-1">
                  {b.title_name}
                </h3>
                <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
                  {b.tag_line}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-auto mb-3">
                  <span className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {b.author}
                  </span>
                  {b.read_time && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {b.read_time}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Tag className="w-3 h-3" />
                    {b.entry_date?.slice(0, 10)}
                  </span>
                </div>
                <div className="flex items-center gap-2 pt-3 border-t border-border">
                  <button
                    onClick={() => setViewBlog(b)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold border border-border text-muted-foreground hover:bg-muted transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5" /> Preview
                  </button>
                  <button
                    onClick={() =>
                      setFormPage({ open: true, editId: b.primary_id })
                    }
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold border border-border text-muted-foreground hover:bg-muted transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  {/* 🆕 Status toggle button */}
                  <button
                    onClick={() => handleToggleStatus(b)}
                    disabled={statusLoadingId === b.primary_id}
                    title={b.status === 1 ? "Deactivate" : "Activate"}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors disabled:opacity-50 ${
                      b.status === 1
                        ? "text-green-600 hover:bg-green-50"
                        : "text-gray-400 hover:bg-muted"
                    }`}
                  >
                    {b.status === 1 ? (
                      <Power className="w-3.5 h-3.5" />
                    ) : (
                      <PowerOff className="w-3.5 h-3.5" />
                    )}
                  </button>
                  <button
                    onClick={() => setDeleteId(b.primary_id)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted flex-shrink-0 transition-colors"
                    style={{ color: "hsl(var(--destructive))" }}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {viewBlog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm p-4"
          onClick={() => setViewBlog(null)}
        >
          <div
            className="section-panel p-0 w-full max-w-lg max-h-[90vh] overflow-y-auto fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="section-panel-header sticky top-0 z-10">
              <h2 className="section-panel-title">Blog Preview</h2>
              <button
                onClick={() => setViewBlog(null)}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="section-panel-body space-y-4">
              {viewBlog.image_url && (
                <img
                  src={viewBlog.image_url}
                  alt={viewBlog.title_name}
                  className="w-full h-48 object-cover rounded-lg"
                />
              )}
              <div className="flex flex-wrap items-center gap-2">
                {viewBlog.category && (
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                    style={{ background: "hsl(var(--accent))" }}
                  >
                    {viewBlog.category}
                  </span>
                )}
                {viewBlog.read_time && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    {viewBlog.read_time}
                  </span>
                )}
              </div>
              <h2 className="text-lg font-bold text-foreground leading-snug">
                {viewBlog.title_name}
              </h2>
              {viewBlog.tag_line && (
                <p className="text-sm text-muted-foreground">
                  {viewBlog.tag_line}
                </p>
              )}
              <div className="flex items-center gap-2 py-2 border-y border-border">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                  style={{ background: "var(--gradient-hero)" }}
                >
                  {viewBlog.author?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    {viewBlog.author}
                  </p>
                  {viewBlog.designation && (
                    <p className="text-xs text-muted-foreground">
                      {viewBlog.designation}
                    </p>
                  )}
                </div>
                <p className="text-xs text-muted-foreground ml-auto">
                  {viewBlog.entry_date?.slice(0, 10)}
                </p>
              </div>
              <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                {viewBlog.description}
              </p>
              <div className="pt-2 flex justify-end">
                <button
                  onClick={() => {
                    setViewBlog(null);
                    setFormPage({ open: true, editId: viewBlog.primary_id });
                  }}
                  className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors text-foreground"
                >
                  <Pencil className="w-4 h-4" /> Edit Blog
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 backdrop-blur-sm"
          onClick={() => setDeleteId(null)}
        >
          <div
            className="section-panel p-0 w-full max-w-sm mx-4 fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="section-panel-header">
              <h2 className="section-panel-title">Delete Blog</h2>
            </div>
            <div className="section-panel-body space-y-4">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete this blog? This action cannot be
                undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteId(null)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold border border-border text-foreground hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
                  style={{ background: "hsl(var(--destructive))" }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
